import React from 'react';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import NoSsr from '@mui/material/NoSsr';
import Zoom from '@mui/material/Zoom';
import useMediaQuery from '@mui/material/useMediaQuery';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import getTheme from '../theme/theme';
import Header from './Header';
import Footer from './Footer';
import { ThemeProvider } from '@mui/material/styles';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps): JSX.Element => {
  const theme = getTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'), {
    defaultMatches: true,
  });

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const scrollTo = (id: string): void => {
    setTimeout(() => {
      const element = document.querySelector(`#${id}`) as HTMLElement;
      if (!element) {
        return;
      }
      window.scrollTo({ left: 0, top: element.offsetTop, behavior: 'smooth' });
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        id="page-top"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Header />
        <Box component="main" sx={{ flex: '1 0 auto', width: '100%', margin: '0 auto' }}>
          {children}
        </Box>
        <Footer />
        <NoSsr>
          <Zoom in={trigger}>
            <Box
              onClick={() => scrollTo('page-top')}
              role="presentation"
              sx={{ position: 'fixed', bottom: 24, right: 32 }}
            >
              <Fab
                color="primary"
                size="small"
                aria-label="scroll back to top"
                sx={{
                  color: theme.palette.common.black,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: theme.palette.primary.main,
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                }}
              >
                <KeyboardArrowUpIcon />
              </Fab>
            </Box>
          </Zoom>
        </NoSsr>
      </Box>
    </ThemeProvider>
  );
};

export default Layout;

